import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import CategoryPage from "./CategoryPage";
import Overline from "../components/Overline";
import Button from "../components/Button";
import { getParshaPortions, resolvePermalink } from "../api/redesignApi";
import { paths } from "../routing/paths";

/*
 * This week's parsha — /weekly-parsha-classes in the redesign. A portion is
 * an archive category, so a single-parsha week renders that portion's
 * redesigned category page directly (all its classes, one click saved).
 * A double parsha renders a chooser: one navy card per portion.
 */

function LoadingState() {
  return (
    <PublicLayout>
      <div className="max-w-[1280px] w-full mx-auto px-5 pt-8 flex flex-col gap-4" aria-busy="true" aria-label="Loading">
        <div className="h-4 w-48 rounded bg-ic-lightGray animate-pulse" />
        <div className="h-10 w-3/4 rounded bg-ic-lightGray animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-ic-lightGray animate-pulse" />
      </div>
    </PublicLayout>
  );
}

const stripParshas = (name) => (name || "").replace(/^Parshas\s+/i, "");

export default function ParshaPage() {
  const [state, setState] = useState({ status: "loading", portions: [], category: null });

  useEffect(() => {
    let cancelled = false;
    getParshaPortions()
      .then(async (portions) => {
        if (cancelled) return;
        if (portions.length === 1) {
          // Single parsha — resolve its category node for the full page.
          const result = await resolvePermalink(portions[0].slug).catch(() => null);
          if (cancelled) return;
          setState({
            status: "done",
            portions,
            category: result?.kind === "category" ? result : null,
          });
        } else {
          setState({ status: "done", portions, category: null });
        }
      })
      .catch(() => !cancelled && setState({ status: "done", portions: [], category: null }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") return <LoadingState />;

  // Single portion resolved — the portion's own redesigned category page.
  if (state.portions.length === 1 && state.category) {
    return <CategoryPage category={state.category} />;
  }

  const heading =
    state.portions.length > 1
      ? `Parshas ${state.portions.map((p) => stripParshas(p.name)).join("-")}`
      : state.portions[0]?.name || "This week's parsha";

  return (
    <PublicLayout>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">This week's parsha</span>
        </nav>

        <div className="flex flex-col gap-2">
          <Overline>This week's Torah portion</Overline>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            {heading}
          </h1>
          {state.portions.length > 1 && (
            <p className="m-0 text-[18px] leading-7 text-ic-body max-w-[720px]">
              A double parsha this week — pick a portion to browse its classes.
            </p>
          )}
        </div>

        {state.portions.length > 0 ? (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))" }}>
            {state.portions.map((p) => (
              <Link
                key={p.id || p.slug}
                to={`/${p.slug}`}
                className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark p-5 flex items-center gap-4 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-elevated-dark focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-ic-navyCard hidden xs:flex flex-col items-center justify-center">
                  <span dir="rtl" className="text-[20px] font-extrabold text-ic-sky leading-6">פרשה</span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <span className="text-[16px] font-semibold leading-6 text-white ic-wrap-anywhere">{p.name}</span>
                  {p.description && p.description !== p.name && (
                    <span className="text-[12px] leading-4 text-ic-lightGray overflow-hidden" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {p.description}
                    </span>
                  )}
                </div>
                <span className="flex-shrink-0 text-ic-sky text-[14px] font-semibold underline">Browse classes</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <h2 className="m-0 text-[28px] font-bold leading-9 tracking-[-0.3px] text-black">
              This week's portion isn't loading
            </h2>
            <p className="m-0 text-[18px] leading-7 text-ic-body max-w-[480px]">
              Every parsha's classes live in the archive — browse by category instead.
            </p>
            <Link to={paths.categories()}>
              <Button size="lg">Browse all categories</Button>
            </Link>
          </div>
        )}
      </main>
    </PublicLayout>
  );
}
